/**
 * Collection sort — slide-out panel + product reorder.
 * Used by Curated Collections (featured-collection-showcase) and Collection editorial grid.
 *
 * FCS: client reorder for title/price/date/manual; server fetch for best-selling.
 * CEG: always server fetch (preserves editorial grid layout).
 */

(function () {
  const PANEL_OPEN_CLASS = "is-open";
  const BODY_LOCK_CLASS = "fcs-sort-scroll-lock";
  const HOST_SELECTOR = "featured-collection-showcase, collection-editorial-grid";
  const SERVER_SORT_VALUES = new Set(["best-selling"]);

  let scrollLockState = null;

  const CLIENT_SORT_COMPARATORS = {
    manual: (a, b) => a.index - b.index,
    "title-ascending": (a, b) => a.title.localeCompare(b.title),
    "title-descending": (a, b) => b.title.localeCompare(a.title),
    "price-ascending": (a, b) => a.price - b.price,
    "price-descending": (a, b) => b.price - a.price,
    "created-ascending": (a, b) => a.created - b.created,
    "created-descending": (a, b) => b.created - a.created,
  };

  function getHostConfig(host) {
    if (host && host.tagName === "COLLECTION-EDITORIAL-GRID") {
      return {
        type: "ceg",
        innerSelector: "[data-ceg-inner]",
        gridSelector: "[data-ceg-rows]",
        cardSelector: "[data-ceg-product-card]",
        alwaysServerSort: true,
        replaceFullInner: true,
      };
    }

    return {
      type: "fcs",
      innerSelector: "[data-fcs-inner]",
      gridSelector: ".fcs__products",
      cardSelector: "[data-fcs-product-card]",
      alwaysServerSort: false,
      replaceFullInner: false,
    };
  }

  function getScrollbarWidth() {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  function lockPageScroll() {
    if (scrollLockState) return;

    const scrollbarWidth = getScrollbarWidth();
    const computedPaddingRight =
      parseFloat(getComputedStyle(document.body).paddingRight) || 0;

    scrollLockState = {
      originalBodyPaddingRight: document.body.style.paddingRight,
      originalBodyOverflow: document.body.style.overflow,
      originalHtmlOverflow: document.documentElement.style.overflow,
    };

    document.body.classList.add(BODY_LOCK_CLASS);
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty(
        "--fcs-scrollbar-width",
        `${scrollbarWidth}px`
      );
      document.body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
    }
  }

  function unlockPageScroll() {
    if (!scrollLockState) return;

    const {
      originalBodyPaddingRight,
      originalBodyOverflow,
      originalHtmlOverflow,
    } = scrollLockState;

    scrollLockState = null;

    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.style.paddingRight = originalBodyPaddingRight;
    document.body.style.overflow = originalBodyOverflow;
    document.documentElement.style.overflow = originalHtmlOverflow;
    document.documentElement.style.removeProperty("--fcs-scrollbar-width");
  }

  function getHostFromNode(node) {
    return node && node.closest ? node.closest(HOST_SELECTOR) : null;
  }

  function getSectionIdFromPanel(panel) {
    if (!panel) return null;

    const fromId = panel.id && panel.id.match(/^fcs-sort-panel-(.+)$/);
    if (fromId) return fromId[1];

    return panel.dataset.sectionId || null;
  }

  function getHostFromPanel(panel) {
    const sectionId = getSectionIdFromPanel(panel);
    if (!sectionId) return null;

    const shopifySection = document.getElementById(
      `shopify-section-${sectionId}`
    );

    if (shopifySection) {
      return shopifySection.querySelector(HOST_SELECTOR);
    }

    const escapedId =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(sectionId)
        : sectionId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

    return document.querySelector(
      `${HOST_SELECTOR}[data-section-id="${escapedId}"]`
    );
  }

  function getFreshInnerFromResponse(doc, sectionId, innerSelector) {
    const sectionRoot =
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(".shopify-section");

    if (sectionRoot) {
      const scopedInner = sectionRoot.querySelector(innerSelector);
      if (scopedInner) return scopedInner;
    }

    return doc.querySelector(innerSelector);
  }

  function getPanelForHost(host) {
    if (!host) return null;

    const sectionId = host.dataset.sectionId;
    if (!sectionId) return null;

    return document.getElementById(`fcs-sort-panel-${sectionId}`);
  }

  function portalPanel(panel) {
    if (!panel || panel.dataset.fcsPortaled === "true") return panel;

    document.body.appendChild(panel);
    panel.dataset.fcsPortaled = "true";

    return panel;
  }

  function setPanelTriggersExpanded(host, expanded) {
    if (!host) return;

    host.querySelectorAll("[data-fcs-sort-open]").forEach((button) => {
      button.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  }

  function closeSortPanel(panel, options = {}) {
    const { unlockScroll = true } = options;
    const resolvedPanel =
      panel && panel.matches && panel.matches("[data-fcs-sort-panel]")
        ? panel
        : getPanelForHost(panel);

    if (!resolvedPanel || !resolvedPanel.classList.contains(PANEL_OPEN_CLASS)) {
      return null;
    }

    const host = getHostFromPanel(resolvedPanel);

    resolvedPanel.classList.remove(PANEL_OPEN_CLASS);
    resolvedPanel.setAttribute("aria-hidden", "true");
    resolvedPanel.setAttribute("inert", "");

    setPanelTriggersExpanded(host, false);

    if (unlockScroll && !document.querySelector(".fcs-sort-panel.is-open")) {
      unlockPageScroll();
    }

    return resolvedPanel;
  }

  function openSortPanel(host) {
    document.querySelectorAll(".fcs-sort-panel.is-open").forEach((openPanel) => {
      closeSortPanel(openPanel, { unlockScroll: false });
    });

    const panel = portalPanel(getPanelForHost(host));
    if (!panel) {
      console.warn(
        "[fcs] Sort panel not found. Expected id:",
        `fcs-sort-panel-${host?.dataset?.sectionId}`
      );
      return;
    }

    panel.classList.add(PANEL_OPEN_CLASS);
    panel.removeAttribute("inert");
    panel.setAttribute("aria-hidden", "false");

    setPanelTriggersExpanded(host, true);
    lockPageScroll();
  }

  function getHostContext(host) {
    const config = getHostConfig(host);

    return {
      host,
      config,
      sectionId: host.dataset.sectionId,
      collectionHandle: host.dataset.collectionHandle,
      inner: host.querySelector(config.innerSelector),
      sortPanel: getPanelForHost(host),
      sortOptions: Array.from(
        getPanelForHost(host)?.querySelectorAll("[data-fcs-sort-value]") || []
      ),
      sortCurrentLabels: Array.from(
        host.querySelectorAll("[data-fcs-sort-current]")
      ),
    };
  }

  function updateSortUi(host, sortBy, label) {
    if (label) {
      host.querySelectorAll("[data-fcs-sort-current]").forEach((el) => {
        el.textContent = label;
      });
    }

    getPanelForHost(host)
      ?.querySelectorAll("[data-fcs-sort-value]")
      .forEach((button) => {
        const selected = button.getAttribute("data-fcs-sort-value") === sortBy;
        button.setAttribute("aria-selected", selected ? "true" : "false");
      });

    if (typeof window.initFcsSortTriggerFrames === "function") {
      window.initFcsSortTriggerFrames(host);
    }
  }

  function getSortLabel(host, sortBy) {
    const panel = getPanelForHost(host);
    const option = panel?.querySelector(
      `[data-fcs-sort-value="${
        typeof CSS !== "undefined" && CSS.escape
          ? CSS.escape(sortBy)
          : sortBy.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
      }"]`
    );

    return option?.textContent?.trim() || "";
  }

  function readCardSortData(card, index) {
    return {
      el: card,
      index: Number.parseInt(
        card.dataset.fcsOriginalIndex ??
          card.dataset.cegOriginalIndex ??
          String(index),
        10
      ),
      title: (
        card.dataset.fcsProductTitle ||
        card.dataset.cegProductTitle ||
        ""
      ).toLowerCase(),
      price: Number.parseFloat(
        card.dataset.fcsProductPrice || card.dataset.cegProductPrice || "0"
      ),
      created: Number.parseInt(
        card.dataset.fcsProductCreated || card.dataset.cegProductCreated || "0",
        10
      ),
    };
  }

  function sortProductsClient(host, sortBy, config) {
    const resolvedConfig = config || getHostConfig(host);
    const compare = CLIENT_SORT_COMPARATORS[sortBy];

    if (!compare || resolvedConfig.alwaysServerSort) return false;

    const grid = host.querySelector(resolvedConfig.gridSelector);
    if (!grid) return false;

    const cards = Array.from(grid.querySelectorAll(resolvedConfig.cardSelector));
    if (cards.length < 2) return false;

    const decorated = cards.map(readCardSortData);
    decorated.sort(compare);
    decorated.forEach(({ el }) => grid.appendChild(el));

    return true;
  }

  function shouldUseServerSort(sortBy, config) {
    if (config?.alwaysServerSort) return true;
    return SERVER_SORT_VALUES.has(sortBy);
  }

  function isValidFreshContent(innerEl, config) {
    if (!innerEl) return false;
    return innerEl.querySelectorAll(config.cardSelector).length > 0;
  }

  function restoreDefaultProductOrder(host, config) {
    sortProductsClient(host, "manual", config);
  }

  function updateCollectionSortUrl(sortBy) {
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set("sort_by", sortBy);
    pageUrl.searchParams.delete("page");
    history.replaceState(
      {},
      "",
      `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`
    );
  }

  function reinitHostMedia(host) {
    if (typeof window.initSpecialFrames === "function") {
      window.initSpecialFrames(host);
    }

    if (typeof window.initFpsProductMedia === "function") {
      window.initFpsProductMedia(host);
    }

    if (typeof window.initFcsSortTriggerFrames === "function") {
      window.initFcsSortTriggerFrames(host);
    }

    if (typeof window.initCegScrollRevealForSection === "function") {
      const sectionRoot =
        host.closest(".shopify-section") ||
        document.getElementById(`shopify-section-${host.dataset.sectionId}`);

      if (sectionRoot) {
        sectionRoot.classList.remove("ceg-scroll-reveal-ready");
        window.initCegScrollRevealForSection();
      }
    }
  }

  function applyServerSortFallback(host, sortBy, label) {
    const config = getHostConfig(host);

    if (config.type === "fcs") {
      restoreDefaultProductOrder(host, config);
    }

    updateSortUi(host, sortBy, label);
  }

  async function applySort(host, sortBy, options = {}) {
    const ctx = getHostContext(host);
    const { config } = ctx;
    const label = options.label || getSortLabel(host, sortBy);

    if (!sortBy || !ctx.sectionId || !ctx.collectionHandle || !ctx.inner) {
      if (shouldUseServerSort(sortBy, config)) {
        applyServerSortFallback(host, sortBy, label);
      }
      return;
    }

    const currentTarget = config.replaceFullInner
      ? ctx.inner
      : host.querySelector(config.gridSelector);

    if (!currentTarget) {
      if (shouldUseServerSort(sortBy, config)) {
        applyServerSortFallback(host, sortBy, label);
      }
      return;
    }

    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    const url = new URL(
      `${root}collections/${ctx.collectionHandle}`,
      window.location.origin
    );

    url.searchParams.set("section_id", ctx.sectionId);
    url.searchParams.set("sort_by", sortBy);

    host._fcsAbortController?.abort();
    host._fcsAbortController = new AbortController();

    if (!options.silent) {
      ctx.inner.setAttribute("aria-busy", "true");
    }

    try {
      const response = await fetch(url.toString(), {
        signal: host._fcsAbortController.signal,
      });

      if (!response.ok) {
        console.warn("[fcs] Sort fetch failed:", response.status, url.toString());
        applyServerSortFallback(host, sortBy, label);
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const freshInner = getFreshInnerFromResponse(
        doc,
        ctx.sectionId,
        config.innerSelector
      );

      if (!isValidFreshContent(freshInner, config)) {
        console.warn(
          "[fcs] Sort response invalid, keeping current product order:",
          ctx.sectionId
        );
        applyServerSortFallback(host, sortBy, label);
        return;
      }

      if (config.replaceFullInner) {
        currentTarget.replaceWith(document.importNode(freshInner, true));
        updateCollectionSortUrl(sortBy);
      } else {
        const freshProducts = freshInner.querySelector(config.gridSelector);

        if (!isValidFreshContent(freshProducts, config)) {
          applyServerSortFallback(host, sortBy, label);
          return;
        }

        currentTarget.replaceWith(document.importNode(freshProducts, true));
      }

      reinitHostMedia(host);
      updateSortUi(host, sortBy, label);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("Collection sort failed", error);
        applyServerSortFallback(host, sortBy, label);
      }
    } finally {
      host.querySelector(config.innerSelector)?.removeAttribute("aria-busy");
    }
  }

  function applySortChoice(host, sortBy, label) {
    updateSortUi(host, sortBy, label);

    const config = getHostConfig(host);

    if (shouldUseServerSort(sortBy, config)) {
      applySort(host, sortBy, { label });
      return;
    }

    if (!sortProductsClient(host, sortBy, config)) {
      applySort(host, sortBy, { label });
    }
  }

  function initHost(host) {
    if (!host || host.dataset.fcsInitialized === "true") return;
    host.dataset.fcsInitialized = "true";

    const panel = getPanelForHost(host);
    if (panel) portalPanel(panel);

    reinitHostMedia(host);

    if (host.dataset.fetchOnLoad === "true" && host.dataset.initialSort) {
      const initialSort = host.dataset.initialSort;
      const initialLabel = getSortLabel(host, initialSort);
      const config = getHostConfig(host);

      if (shouldUseServerSort(initialSort, config)) {
        applySort(host, initialSort, { silent: true, label: initialLabel });
      } else {
        sortProductsClient(host, initialSort, config);
        updateSortUi(host, initialSort, initialLabel);
      }
    }
  }

  function initAll(root) {
    const scope = root && root.nodeType ? root : document;
    scope.querySelectorAll(HOST_SELECTOR).forEach(initHost);
  }

  document.addEventListener("click", (event) => {
    const openTrigger = event.target.closest("[data-fcs-sort-open]");
    if (openTrigger) {
      const host = getHostFromNode(openTrigger);
      if (host) {
        event.preventDefault();
        openSortPanel(host);
      }
      return;
    }

    const closeTrigger = event.target.closest("[data-fcs-sort-close]");
    if (closeTrigger) {
      event.preventDefault();
      const panel = closeTrigger.closest("[data-fcs-sort-panel]");
      if (panel) closeSortPanel(panel);
      return;
    }

    const sortOption = event.target.closest("[data-fcs-sort-value]");
    if (sortOption) {
      const panel = sortOption.closest("[data-fcs-sort-panel]");
      if (!panel) return;

      event.preventDefault();
      const value = sortOption.getAttribute("data-fcs-sort-value");
      const label = sortOption.textContent.trim();
      const host = getHostFromPanel(panel);

      closeSortPanel(panel);

      if (host && value) {
        applySortChoice(host, value, label);
      } else if (!host) {
        console.warn(
          "[fcs] Sort host not found for panel:",
          panel.id || panel.dataset.sectionId
        );
      }
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    const openPanel = document.querySelector(".fcs-sort-panel.is-open");
    if (openPanel) {
      event.preventDefault();
      closeSortPanel(openPanel);
    }
  });

  class FeaturedCollectionShowcase extends HTMLElement {
    connectedCallback() {
      initHost(this);
    }
  }

  class CollectionEditorialGrid extends HTMLElement {
    connectedCallback() {
      initHost(this);
    }
  }

  if (!window.customElements.get("featured-collection-showcase")) {
    window.customElements.define(
      "featured-collection-showcase",
      FeaturedCollectionShowcase
    );
  }

  if (!window.customElements.get("collection-editorial-grid")) {
    window.customElements.define(
      "collection-editorial-grid",
      CollectionEditorialGrid
    );
  }

  window.initFeaturedCollectionShowcase = initAll;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target || document);
  });
})();
